"use client"

import React, { useEffect, useMemo, useRef, useState } from "react"
import { Crosshair, Maximize2 } from "lucide-react"
import { groupColor, type EntityGraph, type GraphEdge, type GraphNode } from "@/lib/entity-graph"
import { centreViewport, fitViewport, layoutExtent, nodeRadius, zoomViewport, type Layout, type Viewport } from "@/lib/entity-layout"
import { useElementSize } from "@/hooks/use-element-size"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

export interface EntityMapCanvasProps {
  graph: EntityGraph
  layout: Layout
  selected: string | null
  /** Node ids on the open blade path (drawn lit) */
  lit: Set<string>
  /** Only the selection and its neighbours are opaque */
  isolate: boolean
  onSelect: (id: string) => void
  onHover: (edge: GraphEdge | null, node: GraphNode | null) => void
}

const LIT = "#ffffff"
const EDGE = "#9db2e0"
const DRAG_THRESHOLD = 4
/** Above this zoom every emphasised node carries its label */
const LABEL_ALL_ZOOM = 1.6

/**
 * The entity map as a pan-and-zoom SVG: a circle per entity (size = field count,
 * colour = group), a line per association (solid to-one, dashed to-many), labels on the
 * selection, its nearest neighbours, the lit path and the hovered node. Edges that do
 * not touch the selection are drawn as a few merged paths so a tenant with hundreds of
 * entities stays smooth.
 */
export function EntityMapCanvas({ graph, layout, selected, lit, isolate, onSelect, onHover }: EntityMapCanvasProps) {
  const { t } = useTranslation()
  const { ref: wrapper, size: measured, element } = useElementSize<HTMLDivElement>()
  const size = measured.width > 0 ? measured : { width: 800, height: 520 }
  const [view, setView] = useState<Viewport>({ x: 400, y: 260, k: 1 })
  const [hovered, setHovered] = useState<string | null>(null)
  const [animate, setAnimate] = useState(true)
  const drag = useRef<{ x: number; y: number; vx: number; vy: number } | null>(null)
  // Whether the pointer moved during the last press, so a pan does not also count as a click
  const panned = useRef(false)
  const fitted = useRef(false)
  // The layout keeps changing while schemas arrive; only a new selection may move the view
  const lastCentred = useRef<string | null>(null)

  // Fit around the connected cluster when there is one; the unconnected catalog ring sits outside until zoomed out
  const extent = useMemo(() => {
    const connected = new Set(graph.edges.flatMap((e) => [e.from, e.to]))
    return layoutExtent(layout, connected.size > 1 ? connected : undefined, connected.size > 1 ? 60 : 40)
  }, [layout, graph.edges])

  // Fit once the size is known; afterwards follow the selection at the current zoom
  useEffect(() => {
    if (fitted.current || layout.size === 0 || measured.width === 0) return
    fitted.current = true
    setView(fitViewport(extent, size.width, size.height))
  }, [extent, layout.size, size, measured.width])

  useEffect(() => {
    const point = selected ? layout.get(selected) : undefined
    if (!point || !fitted.current || selected === lastCentred.current) return
    lastCentred.current = selected
    setAnimate(true)
    setView((current) => centreViewport(current, point, size.width, size.height))
  }, [selected, layout, size])

  const neighbours = useMemo(() => {
    const set = new Set<string>()
    if (!selected) return set
    for (const edge of graph.edges) {
      if (edge.from === selected) set.add(edge.to)
      if (edge.to === selected) set.add(edge.from)
    }
    return set
  }, [graph.edges, selected])

  // Nothing to isolate against until the selection has known neighbours
  const emphasised = (id: string) => !isolate || !selected || neighbours.size === 0 || id === selected || neighbours.has(id) || lit.has(id)

  const labelled = useMemo(() => {
    const ids = new Set<string>(lit)
    if (selected) ids.add(selected)
    if (hovered) ids.add(hovered)
    const origin = selected ? layout.get(selected) : undefined
    if (origin) {
      Array.from(neighbours)
        .map((id) => ({ id, d: distance(origin, layout.get(id)) }))
        .sort((a, b) => a.d - b.d)
        .slice(0, 8)
        .forEach((n) => ids.add(n.id))
    }
    return ids
  }, [lit, selected, hovered, neighbours, layout])

  // Edges touching the selection or the lit path are interactive; the rest merge into four paths
  const { individual, merged } = useMemo(() => {
    const individual: GraphEdge[] = []
    const merged: Record<"solid" | "dashed" | "solidFaded" | "dashedFaded", string[]> = { solid: [], dashed: [], solidFaded: [], dashedFaded: [] }
    for (const edge of graph.edges) {
      const a = layout.get(edge.from)
      const b = layout.get(edge.to)
      if (!a || !b) continue
      const touches = selected !== null && (edge.from === selected || edge.to === selected)
      if (touches || (lit.has(edge.from) && lit.has(edge.to))) {
        individual.push(edge)
        continue
      }
      const faded = isolate && selected !== null && neighbours.size > 0
      const key = `${edge.kind === "to-many" ? "dashed" : "solid"}${faded ? "Faded" : ""}` as keyof typeof merged
      merged[key].push(`M${a.x.toFixed(1)} ${a.y.toFixed(1)}L${b.x.toFixed(1)} ${b.y.toFixed(1)}`)
    }
    return { individual, merged }
  }, [graph.edges, layout, selected, lit, isolate, neighbours])

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return
    drag.current = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y }
    panned.current = false
    setAnimate(false)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current
    if (!d) return
    const dx = e.clientX - d.x
    const dy = e.clientY - d.y
    if (!panned.current && Math.hypot(dx, dy) < DRAG_THRESHOLD) return
    panned.current = true
    setView((current) => ({ ...current, x: d.vx + dx, y: d.vy + dy }))
  }
  const onPointerUp = () => {
    drag.current = null
  }
  const onWheel = (e: React.WheelEvent) => {
    const rect = element?.getBoundingClientRect()
    if (!rect) return
    setAnimate(false)
    setView((current) => zoomViewport(current, Math.exp(-e.deltaY * 0.0015), { x: e.clientX - rect.left, y: e.clientY - rect.top }))
  }
  const clickNode = (id: string) => {
    if (panned.current) return
    onSelect(id)
  }

  const fit = () => {
    setAnimate(true)
    setView(fitViewport(extent, size.width, size.height))
  }
  const centre = () => {
    const point = selected ? layout.get(selected) : undefined
    if (!point) return
    setAnimate(true)
    setView((current) => centreViewport({ ...current, k: Math.max(current.k, 1.2) }, point, size.width, size.height))
  }

  const labelAll = view.k >= LABEL_ALL_ZOOM
  const fontSize = Math.max(9, Math.min(13, 11 / Math.sqrt(view.k)))

  return (
    <div ref={wrapper} className="relative h-full w-full touch-none select-none overflow-hidden">
      <svg
        width={size.width}
        height={size.height}
        className={drag.current ? "cursor-grabbing" : "cursor-grab"}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerUp}
        onWheel={onWheel}
        role="img"
        aria-label={t("explorer.entityMap.title")}
      >
        <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`} className={animate ? "motion-safe:transition-transform motion-safe:duration-500 motion-safe:ease-out" : undefined}>
          <path d={merged.solidFaded.join("")} stroke={EDGE} strokeOpacity={0.07} strokeWidth={1 / view.k} fill="none" />
          <path d={merged.dashedFaded.join("")} stroke={EDGE} strokeOpacity={0.07} strokeWidth={1 / view.k} strokeDasharray={`${5 / view.k} ${4 / view.k}`} fill="none" />
          <path d={merged.solid.join("")} stroke={EDGE} strokeOpacity={0.35} strokeWidth={1 / view.k} fill="none" />
          <path d={merged.dashed.join("")} stroke={EDGE} strokeOpacity={0.35} strokeWidth={1 / view.k} strokeDasharray={`${5 / view.k} ${4 / view.k}`} fill="none" />

          {individual.map((edge) => {
            const a = layout.get(edge.from)!
            const b = layout.get(edge.to)!
            const isLit = lit.has(edge.from) && lit.has(edge.to)
            return (
              <g key={edge.id} onMouseEnter={() => onHover(edge, null)} onMouseLeave={() => onHover(null, null)}>
                <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="transparent" strokeWidth={10 / view.k} />
                <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={isLit ? LIT : "#c7d2f0"} strokeOpacity={isLit ? 0.95 : 0.7} strokeWidth={(isLit ? 2 : 1.4) / view.k} strokeDasharray={edge.kind === "to-many" ? `${5 / view.k} ${4 / view.k}` : undefined} />
              </g>
            )
          })}

          {graph.nodes.map((node) => {
            const p = layout.get(node.id)
            if (!p) return null
            const isLit = lit.has(node.id)
            const isSelected = node.id === selected
            const dim = !emphasised(node.id)
            const r = nodeRadius(node) * (isSelected ? 1.3 : hovered === node.id ? 1.2 : 1)
            const showLabel = !dim && (labelled.has(node.id) || labelAll)
            return (
              <g
                key={node.id}
                transform={`translate(${p.x} ${p.y})`}
                opacity={dim ? 0.3 : 1}
                className="motion-safe:transition-[transform,opacity] motion-safe:duration-500 motion-safe:ease-out cursor-pointer"
                onClick={() => clickNode(node.id)}
                onMouseEnter={() => {
                  setHovered(node.id)
                  onHover(null, node)
                }}
                onMouseLeave={() => {
                  setHovered(null)
                  onHover(null, null)
                }}
              >
                {(isLit || isSelected) && <circle r={r * 1.6} fill="#9db2e0" fillOpacity={0.18} />}
                <circle
                  r={r}
                  fill={isLit || isSelected ? LIT : groupColor(node.group)}
                  fillOpacity={node.resolved || isSelected ? 1 : 0.7}
                  stroke={isSelected ? "#3f5fb8" : node.resolved ? "none" : "#ffffff"}
                  strokeOpacity={node.resolved ? 1 : 0.5}
                  strokeWidth={isSelected ? 2 / view.k : 1 / view.k}
                  strokeDasharray={!node.resolved && !isSelected ? "2 2" : undefined}
                />
                {showLabel && (
                  <text
                    y={r + fontSize + 3}
                    textAnchor="middle"
                    fontSize={fontSize}
                    fontWeight={isSelected ? 700 : 600}
                    fill={isSelected ? LIT : "#e7ebf5"}
                    stroke="#0f214b"
                    strokeWidth={3}
                    paintOrder="stroke"
                    strokeLinejoin="round"
                    className="pointer-events-none font-sans"
                  >
                    {node.label}
                  </text>
                )}
              </g>
            )
          })}
        </g>
      </svg>

      <div className="absolute bottom-3 right-3 flex gap-1.5">
        <button type="button" onClick={fit} title={t("explorer.entityMap.fit")} className="rounded-pill bg-[#0b1735]/80 p-1.5 text-[#e7ebf5] hover:bg-[#0b1735]">
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={centre} disabled={!selected} title={t("explorer.entityMap.centre")} className="rounded-pill bg-[#0b1735]/80 p-1.5 text-[#e7ebf5] hover:bg-[#0b1735] disabled:opacity-40">
          <Crosshair className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

function distance(a: { x: number; y: number }, b?: { x: number; y: number }): number {
  if (!b) return Number.POSITIVE_INFINITY
  return Math.hypot(a.x - b.x, a.y - b.y)
}
